import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button, useToast } from '@centraoneplus/ui';
import {
  ticketService,
  type CreateTicketDto,
  type UpdateTicketDto,
} from '@services/ticket.service';
import { TicketForm, type TicketFormValues } from '@components/tickets/TicketForm';
import { TicketTypeSelector, SelectedTicketTypeCard } from '@components/tickets/TicketTypeSelector';
import type { TicketType } from '@centraoneplus/shared-types';
import { ticketTypesService } from '@services/ticket-types.service';
import { lineItemsService } from '@components/tickets';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { contactPhoneOf } from '@/lib/phone';

/**
 * Tickets created from email/chat often store an HTML body in `description`.
 * The edit form uses a plain <textarea>, so render that HTML down to readable
 * text (preserving line breaks) instead of showing raw tags. Plain-text
 * descriptions are returned untouched.
 */
function htmlToPlainText(value?: string): string {
  if (!value) return '';
  // No markup and no comments → leave as-is.
  if (!/<[a-z!][\s\S]*>/i.test(value) && !value.includes('<!--')) return value;
  const cleaned = value
    // Drop <style>/<script> blocks entirely — their CSS/JS would otherwise leak
    // into textContent (e.g. Outlook's "p { margin-top:0 }" rules).
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    // Drop HTML comments (incl. the <!-- --> wrapper around legacy style blocks).
    .replace(/<!--[\s\S]*?-->/g, '')
    // Preserve line breaks.
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n');
  const tmp = document.createElement('div');
  tmp.innerHTML = cleaned;
  const text = tmp.textContent || '';
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Write the edited custom-field values into the ticket's line item so the Line
 * Items tab stays in sync with the edit form.
 *
 * - Updates keys the line item already has (matched ignoring case/underscores,
 *   so `brokerEmail` ↔ `broker_email` line up) AND adds any new fields — e.g.
 *   when the ticket type is switched and new fields (mpan, eac, …) are filled.
 * - Preserves the line item's own key casing on updates, and leaves untouched
 *   any line-item-only fields not present in the form.
 * - Only syncs when there is exactly ONE line item; with multiple, each holds
 *   its own distinct values (per meter) and the single form can't disambiguate.
 * - Best-effort: the ticket update has already succeeded, so this never throws.
 */
async function syncLineItemsFromCustomFields(
  ticketId: string,
  customFields: Record<string, any>,
): Promise<void> {
  try {
    const items = await lineItemsService.getLineItems(ticketId);
    if (!Array.isArray(items) || items.length !== 1) return;

    const normalize = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');
    const item = items[0];
    const details = { ...((item.lineItemDetails as Record<string, any>) || {}) };

    // Map existing detail keys by normalized form so we update them in place
    // (keeping the line item's casing) instead of creating duplicates.
    const existingKeyByNorm: Record<string, string> = {};
    for (const k of Object.keys(details)) existingKeyByNorm[normalize(k)] = k;

    let changed = false;
    for (const [formKey, value] of Object.entries(customFields || {})) {
      if (formKey.startsWith('_') || value === undefined) continue; // skip internal/AI metadata
      // Reuse the existing key if the line item already has an equivalent one,
      // otherwise add the field under the form's key.
      const targetKey = existingKeyByNorm[normalize(formKey)] || formKey;
      if (details[targetKey] !== value) {
        details[targetKey] = value;
        existingKeyByNorm[normalize(formKey)] = targetKey;
        changed = true;
      }
    }

    if (changed) {
      await lineItemsService.updateLineItem(item.id, { lineItemDetails: details });
    }
  } catch (err) {
    console.warn('Line-item sync failed (ticket already saved):', err);
  }
}

/**
 * Single page that handles both creating a ticket (/tickets/new) and editing an
 * existing one (/tickets/:id/edit). The mode is derived from the route param:
 * with an `id` it loads + prefills + PATCHes; without one it starts blank +
 * POSTs. Both share the same {@link TicketForm}.
 *
 * Create is two screens: /tickets/new shows only the ticket-type grid, and
 * picking a type moves to /tickets/new?type=<id>, the details form. The type
 * lives in the URL so Back returns to the grid and the form survives a reload.
 */
export default function TicketFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = Boolean(id);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const createTypeId = isEdit ? null : searchParams.get('type');
  const isPickingType = !isEdit && !createTypeId;
  // Create, details step: the chosen type, loaded for the summary card.
  const [createType, setCreateType] = useState<TicketType | null>(null);

  const [initialValues, setInitialValues] = useState<Partial<TicketFormValues> | null>(
    isEdit ? null : {},
  );
  // The ticket's OWN stored custom fields (unmerged). initialValues.customFields
  // additionally folds in line-item-derived fields for display/prefill, but on
  // save we overlay only the user's changes onto THIS baseline so line-item
  // fields the user never touched aren't written back as phantom changes.
  const [ticketCustomFields, setTicketCustomFields] = useState<Record<string, any>>({});
  const [ticketNumber, setTicketNumber] = useState<string | number | undefined>();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Edit mode: keep Save disabled until the user actually changes something.
  const [isDirty, setIsDirty] = useState(false);

  // Create, details step: resolve the type from the URL. One that no longer
  // exists or was switched off sends the user back to the picker.
  useEffect(() => {
    if (!createTypeId) {
      setCreateType(null);
      return;
    }
    let cancelled = false;
    setCreateType(null);
    ticketTypesService
      .getTicketType(createTypeId)
      .then((type) => {
        if (cancelled) return;
        if (type?.isActive === false) throw new Error('inactive');
        setCreateType(type);
        setInitialValues({ ticketTypeId: type.id });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Ticket type unavailable', 'Please choose a ticket type');
        navigate('/tickets/new', { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // toast is a fresh object each render; depending on it would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTypeId, navigate]);

  const handlePickType = (type: TicketType | null) => {
    if (!type) return;
    navigate(`/tickets/new?type=${encodeURIComponent(type.id)}`, { state: { fromTypePicker: true } });
  };

  // Back to the grid. Pop history when we came from it, so the stack doesn't
  // grow picker → form → picker; a deep link to the form has nothing to pop.
  const handleChangeType = () => {
    if ((location.state as { fromTypePicker?: boolean } | null)?.fromTypePicker) navigate(-1);
    else navigate('/tickets/new', { replace: true });
  };

  // Edit mode: load the ticket once and seed the form's initial values. The
  // form reads initialValues only on mount, so we hold off rendering it until
  // they're ready.
  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      try {
        const ticket = (await ticketService.getTicket(id)) as any;
        if (cancelled) return;

        // Custom-field values may live on the ticket OR on its line items
        // (AI-extracted per meter, e.g. mpan/eac/kva). Merge both so the form
        // prefills either way. Line items WIN on collisions: they're the store
        // the user edits directly (Line Items tab), so they hold the freshest
        // values, whereas ticket.customFields only changes via the edit form
        // and can go stale after a line-item edit.
        let lineItemFields: Record<string, any> = {};
        try {
          const items = await lineItemsService.getLineItems(id);
          if (!cancelled && Array.isArray(items)) {
            for (const item of items) {
              lineItemFields = { ...lineItemFields, ...(item.lineItemDetails ?? {}) };
            }
          }
        } catch {
          // Line items are best-effort — never block the edit form on them.
        }
        if (cancelled) return;

        setTicketNumber(ticket.ticketNumber);
        setTicketCustomFields((ticket.customFields as Record<string, any>) ?? {});
        setInitialValues({
          subject: ticket.subject ?? '',
          description: htmlToPlainText(ticket.description) || undefined,
          channel: ticket.channel ?? 'email',
          priority: ticket.priority ?? 'medium',
          // Email/chat tickets may carry the contact on the linked customer
          // record rather than the flat fields — fall back so it still fills.
          contactEmail: ticket.contactEmail ?? ticket.customer?.email ?? undefined,
          contactName: ticket.contactName ?? ticket.customer?.name ?? undefined,
          contactPhone: contactPhoneOf(ticket),
          tags: Array.isArray(ticket.tags) ? ticket.tags : [],
          // The API may return the type as a flat id or only as the nested
          // relation — accept either so the custom-field schema loads.
          ticketTypeId: ticket.ticketTypeId ?? ticket.ticketType?.id ?? undefined,
          // Current assignee (flat id or nested relation) so the Assignee
          // dropdown shows who it's assigned to.
          assigneeId: ticket.assigneeId ?? ticket.assignee?.id ?? undefined,
          // Sidebar-parity fields (edit mode). Accept flat ids or nested
          // relations so the selectors preselect the ticket's current values.
          status: ticket.status ?? undefined,
          pipelineId: ticket.pipelineId ?? ticket.pipeline?.id ?? undefined,
          stageId: ticket.stageId ?? ticket.stage?.id ?? undefined,
          customFields: {
            ...((ticket.customFields as Record<string, any>) ?? {}),
            ...lineItemFields,
          },
        });
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.response?.data?.message || err?.message || 'Failed to load ticket');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const handleSubmit = async (values: TicketFormValues) => {
    setIsSaving(true);
    try {
      if (isEdit && id) {
        // Send ONLY what actually changed since the form loaded. The edit page
        // otherwise PATCHes the whole form every save, which makes the backend
        // history diff record an entry for every field — including line-item
        // fields merged in for display — even when a single value changed. This
        // mirrors the sidebar, which patches one field at a time.
        const initial = (initialValues ?? {}) as Partial<TicketFormValues>;
        const changed = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b);
        const dto: UpdateTicketDto = {};

        if (changed(values.subject, initial.subject)) dto.subject = values.subject;
        if (changed(values.description, initial.description)) dto.description = values.description;
        if (changed(values.priority, initial.priority)) dto.priority = values.priority;
        if (changed(values.tags, initial.tags)) dto.tags = values.tags;
        if (changed(values.status, initial.status)) dto.status = values.status;
        // Blank clears the number. Sent only if it changed, so one filled in from
        // the customer isn't copied onto the ticket by an unrelated save.
        if (changed(values.contactPhone ?? '', initial.contactPhone ?? '')) {
          dto.contactPhone = values.contactPhone ?? '';
        }
        // Pipeline/stage mirror the sidebar. Send pipeline changes as-is; when the
        // pipeline changed the Stage selector was reset, so stageId won't also be
        // sent and the backend lands the ticket on the new pipeline's first stage.
        if (changed(values.pipelineId ?? null, initial.pipelineId ?? null)) {
          dto.pipelineId = values.pipelineId ?? null;
        }
        if (changed(values.stageId ?? null, initial.stageId ?? null)) {
          dto.stageId = values.stageId ?? null;
        }
        // Clearing the type sends null so the API unsets it.
        if (changed(values.ticketTypeId ?? null, initial.ticketTypeId ?? null)) {
          dto.ticketTypeId = values.ticketTypeId ?? null;
        }

        // Custom fields: collect only the keys the user changed, then overlay
        // them on the ticket's OWN stored fields — never on the line-item-merged
        // set — so untouched line-item fields don't surface as phantom changes.
        const initialCf = (initial.customFields as Record<string, any>) ?? {};
        const newCf = (values.customFields as Record<string, any>) ?? {};
        const changedCfKeys = Object.keys(newCf).filter((k) => changed(newCf[k], initialCf[k]));
        if (changedCfKeys.length > 0) {
          const overlay: Record<string, any> = { ...ticketCustomFields };
          for (const k of changedCfKeys) overlay[k] = newCf[k];
          dto.customFields = overlay;
        }

        await ticketService.updateTicket(id, dto);

        // Assignment goes through the dedicated assign/unassign endpoint (which
        // records history + fires events), not the generic PATCH. Only call it
        // when the assignee actually changed. Clearing it sends null → unassign.
        if (changed(values.assigneeId ?? null, initial.assigneeId ?? null)) {
          await ticketService.assignTicket(id, values.assigneeId ?? null);
        }

        // Keep the ticket's line item in sync with the edited custom fields.
        await syncLineItemsFromCustomFields(id, values.customFields);
        toast.success('Ticket updated', 'Your changes have been saved');
        // Pop the edit page off history so back goes to the ticket → then list,
        // not back into the editor.
        navigate(-1);
      } else {
        const dto: CreateTicketDto = {
          subject: values.subject,
          description: values.description,
          channel: values.channel,
          priority: values.priority,
          contactEmail: values.contactEmail,
          contactName: values.contactName,
          contactPhone: values.contactPhone,
          customerId: values.customerId,
          tags: values.tags,
          ticketTypeId: values.ticketTypeId,
          // Omit entirely when no assignee is chosen so the backend runs its
          // auto-assignment (routing rules / round-robin / agent self-assign).
          // Sending an empty string would fail the API's @IsUUID and 400.
          assigneeId: values.assigneeId || undefined,
          customFields: values.customFields,
        };
        const ticket = await ticketService.createTicket(dto);
        toast.success(
          'Ticket created',
          ticket.ticketNumber ? `#${ticket.ticketNumber} has been created` : 'New ticket has been created',
        );
        // Open the new ticket so the agent can start working on it immediately.
        // `replace` swaps the create page out of history so the detail page's
        // Back goes to where the form was opened from (board/list), not back
        // into the now-submitted create form.
        navigate(`/tickets/${ticket.id}`, { replace: true });
      }
    } catch (err: any) {
      // Validation failures arrive as an array of messages; a toast given an
      // array shows nothing, which read as the form silently doing nothing.
      const apiMessage = err?.response?.data?.message;
      toast.error(
        isEdit ? 'Failed to update ticket' : 'Failed to create ticket',
        (Array.isArray(apiMessage) ? apiMessage.join(', ') : apiMessage) || err?.message || 'Unknown error',
      );
      setIsSaving(false);
    }
  };

  // Go back the way we came (detail → list) rather than pushing a new entry,
  // which would otherwise leave the editor sitting in the history stack.
  const handleCancel = () => navigate(-1);

  return (
    // Floating cards on the canvas, matching the ticket detail page.
    <div className="scrollbar-hide h-full overflow-y-auto bg-[var(--co-canvas)]">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-[var(--co-canvas)] pr-4 pt-4 pb-[14px]">
        <header
          className="min-w-0 border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-3"
          style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
        >
          {/* One row: back + title on the left; submit + utility cluster on the right. */}
          <div className="flex flex-wrap items-center justify-between gap-[10px]">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={handleCancel} className="rounded-[10px]">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-[var(--co-ink)] truncate">
                  {isEdit ? 'Edit Ticket' : 'Create Ticket'}
                  {isEdit && ticketNumber != null && (
                    <span className="text-[var(--co-ink-3)]"> #{ticketNumber}</span>
                  )}
                </h1>
                <p className="text-xs text-[var(--co-ink-3)]">
                  {isEdit
                    ? 'Update this ticket’s details and custom fields.'
                    : isPickingType
                      ? 'Choose a ticket type to continue.'
                      : 'Fill in the details to open a new ticket.'}
                </p>
              </div>
            </div>
            <div className="flex flex-none flex-wrap items-center gap-[10px]">
              {/* Submit is wired to the form via the shared id. */}
              {!loadError && initialValues && !isPickingType && (
              <button
                type="submit"
                form="ticket-form"
                disabled={isSaving || (isEdit && !isDirty)}
                title={isEdit && !isDirty ? 'Make a change to enable saving' : undefined}
                className="btn-primary whitespace-nowrap rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving
                  ? isEdit
                    ? 'Saving...'
                    : 'Creating...'
                  : isEdit
                    ? 'Save Changes'
                    : 'Create Ticket'}
              </button>
              )}
              {!loadError && initialValues && !isPickingType && (
                <span aria-hidden="true" className="mx-1 h-5 w-px flex-none bg-[var(--co-line)]" />
              )}
              <div className="flex h-[34px] flex-none items-center">
                <PageHeaderMeta />
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Body */}
      <div className="pb-4 pr-4">
        {loadError ? (
          <div className="rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)] p-8 flex flex-col items-center justify-center text-center">
            <p className="text-gray-900 dark:text-white font-medium mb-3">{loadError}</p>
            <Button variant="outline" onClick={handleCancel}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go back
            </Button>
          </div>
        ) : isPickingType ? (
          <div className="rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)] p-5 sm:p-6">
            <TicketTypeSelector onChange={handlePickType} required autoSelectDefault={false} />
          </div>
        ) : !initialValues || (!isEdit && !createType) ? (
          <div className="rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)] p-12 flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
          </div>
        ) : (
          <TicketForm
            key={createTypeId ?? id}
            mode={isEdit ? 'edit' : 'create'}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isSaving}
            variant="page"
            onDirtyChange={setIsDirty}
            ticketTypeSlot={
              !isEdit && createType ? (
                <SelectedTicketTypeCard ticketType={createType} onChange={handleChangeType} disabled={isSaving} />
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
